import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Building2, Globe, Inbox, KeyRound, LayoutDashboard, Mail, MapPin, Pencil, Phone, RefreshCw, ShieldCheck, Star, Trash2, TrendingUp, Users, X } from "lucide-react";
import {
  brokeragesApi,
  brokerageAffiliationsApi,
  BROKERAGE_STATUS_BADGE,
  type BrokerageDetailDto,
  type BrokerageStatus,
  type BrokerageAffiliationDto,
  type PartnerKind,
} from "@/api/brokerages";
import { brokersApi } from "@/api/brokers";
import { brokerPickerLabel } from "@/lib/brokerDisplay";
import { leadsApi } from "@/api/leads";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { EntityTabBar, CROSS_CUTTING_TAB_ICONS, type EntityTabItem } from "@/components/layout/EntityTabBar";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { formatCalendarDate, useDateFormatter } from "@/utils/datetime";
import { brokerageKeys } from "@/hooks/brokerages/brokerageKeys";
import { BrokerageStatusModal } from "./BrokerageStatusModal";
import { BrokerConversionModal } from "@/components/brokers/BrokerConversionModal";
import { BrokerageStageStepper } from "@/components/brokerages/BrokerageStageStepper";
import { BrokerageStageAdvancePanel } from "@/components/brokerages/BrokerageStageAdvancePanel";
import { ProductionTab } from "@/components/brokerages/ProductionTab";
import { CertificationsTab } from "@/components/certifications/CertificationsTab";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { BrokerageContactsCard } from "@/components/brokerages/BrokerageContactsCard";
import { PrequalificationSiteTab } from "@/components/partners/PrequalificationSiteTab";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import {
  BrokeragePortalNotificationsTab,
  BrokeragePortalReviewTab,
} from "@/components/brokerages/BrokeragePortalTabs";
import { NeedsAttentionBadge } from "@/components/portalAbsorption/NeedsAttentionBadge";
import { PortalAccountTab } from "@/components/portalAbsorption/PortalAccountTab";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useBrokeragePortalAccount,
  useInviteBrokeragePortal,
} from "@/hooks/brokerages/useBrokeragePortalAccount";

type TabKey =
  | "overview" | "portal-account" | "portal-review" | "portal-notifications"
  | "certification" | "production" | "partners" | "prequalification"
  | "documents" | "tasks" | "calendar" | "messages" | "communications" | "notes"
  | "activity";
// Party-surface §2.1 tab standard — Overview · Portal account · Review queue
// & requests · Notifications · (entity-specific tabs) · the cross-cutting
// tabs always last in the standard order.
const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabKey>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview ?? LayoutDashboard },
  { key: "portal-account", label: "Portal account", icon: KeyRound },
  // Portal absorption (WS-D1) — the firm's collection review queue +
  // delivered-notifications feed, scoped by its linked portal account.
  { key: "portal-review", label: "Review queue & requests", icon: Inbox },
  { key: "portal-notifications", label: "Notifications", icon: Bell },
  { key: "certification", label: "Certification", icon: ShieldCheck },
  { key: "production", label: "Production", icon: TrendingUp },
  { key: "partners", label: "Affiliated Partners", icon: Users },
  // The firm's shared prequalification site + its completions, and any member
  // brokers running their own. Gated Brokerages.Read server-side (the same
  // permission that opens this page), so no per-tab gate is needed.
  { key: "prequalification", label: "Prequalification", icon: Globe },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
];
const TAB_KEYS: ReadonlySet<TabKey> = new Set(TAB_ITEMS.map((t) => t.key));
const isTabKey = (v: string | null): v is TabKey =>
  v !== null && TAB_KEYS.has(v as TabKey);

export function BrokerageDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const { date } = useDateFormatter();
  const canUpdate = hasPermission("Brokerages.Update");
  const canDelete = hasPermission("Brokerages.Delete");
  const canArchive = hasPermission("Brokerages.Archive");

  const [params, setParams] = useSearchParams();
  const tab: TabKey = isTabKey(params.get("tab")) ? (params.get("tab") as TabKey) : "overview";
  const setTab = (next: TabKey) => {
    const updated = new URLSearchParams(params);
    if (next === "overview") updated.delete("tab"); else updated.set("tab", next);
    setParams(updated, { replace: true });
  };

  const [statusOpen, setStatusOpen] = useState(false);
  const [stageTarget, setStageTarget] = useState<BrokerageStatus | null>(null);

  const detail = useQuery({
    queryKey: ["brokerage", id],
    queryFn: () => brokeragesApi.get(id),
    enabled: !!id,
  });

  // Portal absorption (WS-D1) — the firm's linked portal login (feeds the
  // header needs-attention badge + the Portal account tab; the review /
  // notifications tabs resolve it themselves).
  const portalAccount = useBrokeragePortalAccount(id);
  const toast = useToast();
  // Invite = the firm's portal-invite endpoint; the hook owns invalidation
  // (portal-account lookup + portalAbsorptionKeys.all).
  const invite = useInviteBrokeragePortal(id);

  // Default breadcrumb is URL-derived, which renders the raw brokerage GUID as
  // the trailing crumb (and leaves "Brokerages" unlinked). Override with the
  // brokerage's name and a link back to the list once loaded.
  useBreadcrumbs(
    detail.data
      ? [
          { label: "Brokerages", href: "/brokerages" },
          { label: detail.data.name || "Brokerage" },
        ]
      : null,
  );

  if (detail.isLoading) return <p className="text-gray-mid">Loading…</p>;
  if (detail.isError || !detail.data) {
    return <p className="text-status-error" role="alert">Brokerage not found.</p>;
  }
  const b = detail.data;

  return (
    <>
      <PageHeader
        eyebrow="Brokerage"
        title={b.name}
        actions={
          <div className="flex items-center gap-space-2">
            {/* Create-in-context for a prequalification site moved OFF the
                header and onto the Prequalification tab, where the empty state
                that motivates it lives. Same component, same launch query. */}
            {canUpdate && (
              <Button size="sm" variant="secondary" onClick={() => navigate(`/brokerages/${id}/edit`)}>
                <Pencil size={14} strokeWidth={1.5} aria-hidden /> Edit
              </Button>
            )}
            {(canUpdate || canArchive || canDelete) && (
              <Button size="sm" onClick={() => setStatusOpen(true)}>
                <RefreshCw size={14} strokeWidth={1.5} aria-hidden /> Change status
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-space-3 -mt-space-3 mb-space-3">
        <Badge status={BROKERAGE_STATUS_BADGE[b.status]}>{b.status}</Badge>
        <NeedsAttentionBadge portalUserId={portalAccount.data?.portalUserId} />
        <span className="text-caption text-gray-mid">
          Created {date(b.createdAt)}
          {b.modifiedAt ? ` · Modified ${date(b.modifiedAt)}` : ""}
          {b.certifiedAt ? ` · Certified ${date(b.certifiedAt)}` : ""}
        </span>
      </div>

      {/* Brokerage Certification & Pipeline — clickable stage stepper. Advisory
          only; the checklist behind a click never blocks the transition. */}
      <div className="mb-space-4">
        <BrokerageStageStepper
          status={b.status}
          canUpdate={canUpdate}
          onStageClick={setStageTarget}
        />
      </div>

      <EntityTabBar
        ariaLabel="Brokerage sections"
        testIdPrefix="brokerage"
        items={TAB_ITEMS}
        value={tab}
        onChange={setTab}
        className="mb-space-4"
      />

      {tab === "overview" && (
        <div className="space-y-space-4">
          <OverviewTab brokerage={b} />
          <BrokerageContactsCard brokerage={b} canUpdate={canUpdate} />
        </div>
      )}
      {tab === "portal-account" && (
        <PortalAccountTab
          portalUserId={portalAccount.data?.portalUserId ?? null}
          emptyHint={
            b.email
              ? "No portal account is linked to this firm yet."
              : "Add an email to the firm record before sending a portal invitation."
          }
          invite={
            canUpdate && b.email
              ? {
                  send: () =>
                    invite.mutate(undefined, {
                      onSuccess: (r) =>
                        toast.success(
                          r.isExistingAccount
                            ? "Invitation re-sent to the existing account."
                            : `Portal invitation sent to ${r.email}.`,
                        ),
                      onError: (e) => toast.error(extractErrorMessage(e)),
                    }),
                  pending: invite.isPending,
                }
              : undefined
          }
        />
      )}
      {tab === "certification" && (
        <CertificationsTab
          entityType="Brokerage"
          entityId={id}
          currentStage={b.status}
          onCertificationChange={() => {
            qc.invalidateQueries({ queryKey: brokerageKeys.stageReadiness(id) });
          }}
        />
      )}
      {tab === "production" && <ProductionTab brokerageId={id} />}
      {tab === "partners" && <PartnersTab brokerageId={id} brokerageName={b.name} canUpdate={canUpdate} />}
      {tab === "prequalification" && (
        <PrequalificationSiteTab owner={{ kind: "brokerage", id, name: b.name }} />
      )}
      {tab === "portal-review" && <BrokeragePortalReviewTab brokerage={b} />}
      {tab === "portal-notifications" && <BrokeragePortalNotificationsTab brokerage={b} />}
      {/* Brokerage CRM parity — the standard cross-cutting surfaces, wired to
          the same shared components/services the Broker detail page uses. */}
      {tab === "documents" && (
        <DocumentPanel ownerType="Brokerage" ownerId={id} rootName={b.name} />
      )}
      {tab === "tasks" && (
        <TaskListMini
          relatedEntityType="Brokerage"
          relatedEntityId={id}
          addButtonLabel="+ Add task linked to this Brokerage"
        />
      )}
      {tab === "calendar" && (
        <AppointmentListMini
          relatedEntityType="Brokerage"
          relatedEntityId={id}
          entityLabel={b.name}
        />
      )}
      {tab === "messages" && (
        <RelatedConversationsPanel resourceType="brokerage" resourceId={id} entityLabel={b.name} />
      )}
      {tab === "communications" && (
        <CommunicationsTabPanel ownerType="brokerage" ownerId={id} ownerLabel={b.name} />
      )}
      {tab === "notes" && <NotesTabPanel ownerType="brokerage" ownerId={id} />}
      {tab === "activity" && <ActivityTabPanel ownerType="brokerage" ownerId={id} />}

      {stageTarget && (
        <BrokerageStageAdvancePanel
          brokerage={{ id: b.id, name: b.name, status: b.status }}
          target={stageTarget}
          onClose={() => setStageTarget(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: brokerageKeys.legacyDetail(id) })}
        />
      )}

      {statusOpen && (
        <BrokerageStatusModal
          brokerage={b}
          canDelete={canDelete}
          onClose={() => setStatusOpen(false)}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ["brokerage", id] });
            qc.invalidateQueries({ queryKey: ["brokerages"] });
          }}
          onDeleted={() => {
            qc.invalidateQueries({ queryKey: ["brokerages"] });
            navigate("/brokerages");
          }}
        />
      )}
    </>
  );
}

function OverviewTab({ brokerage }: { brokerage: BrokerageDetailDto }) {
  const address = [brokerage.addressLine1, brokerage.city, brokerage.state, brokerage.postalCode]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="grid gap-space-4 max-w-3xl">
      <section>
        <h3 className="font-display font-bold text-h4 text-onyx mb-space-2">Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 border border-border-light rounded-md p-space-3 bg-white-pure">
          <Field label="DBA" value={brokerage.dba} />
          <Field icon={Phone} label="Phone" value={brokerage.phone} />
          <Field icon={Mail} label="Email" value={brokerage.email} />
          <Field icon={Globe} label="Website" value={brokerage.website} />
          <Field label="Primary contact" value={brokerage.primaryContactName} />
          <Field icon={MapPin} label="Address" value={address} />
        </div>
      </section>

      {brokerage.notes && (
        <section>
          <h3 className="font-display font-bold text-h4 text-onyx mb-space-2">Notes</h3>
          <div className="border border-border-light rounded-md p-space-3 bg-white-pure text-small text-onyx whitespace-pre-wrap break-words">
            {brokerage.notes}
          </div>
        </section>
      )}

      <div className="text-caption text-gray-mid flex gap-space-4">
        <span>{brokerage.brokerCount} broker(s)</span>
        <span>{brokerage.leadCount} lead(s)</span>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  icon: Icon,
}: { label: string; value?: string | null; icon?: React.ComponentType<{ size: number; strokeWidth: number; "aria-hidden": true }> }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-space-1 text-caption uppercase tracking-wide text-gray-mid mb-space-1">
        {Icon && <Icon size={12} strokeWidth={1.5} aria-hidden />}
        {label}
      </div>
      <div className="text-small text-onyx break-words">{value || "—"}</div>
    </div>
  );
}

function PartnersTab({ brokerageId, brokerageName, canUpdate }: { brokerageId: string; brokerageName: string; canUpdate: boolean }) {
  const qc = useQueryClient();
  const { date } = useDateFormatter();
  const dialogs = useDialogs();
  const [includeEnded, setIncludeEnded] = useState(false);
  const [adding, setAdding] = useState(false);

  const queryKey = ["brokerage-partners", brokerageId, includeEnded];
  const list = useQuery({
    queryKey,
    queryFn: () => brokerageAffiliationsApi.listForBrokerage(brokerageId, includeEnded),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["brokerage-partners", brokerageId] });

  const setPrimary = useMutation({ mutationFn: (id: string) => brokerageAffiliationsApi.setPrimary(id), onSuccess: invalidate });
  const clearPrimary = useMutation({ mutationFn: (id: string) => brokerageAffiliationsApi.clearPrimary(id), onSuccess: invalidate });
  const end = useMutation({
    mutationFn: (id: string) => brokerageAffiliationsApi.end(id, { endDate: new Date().toISOString() }),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: (id: string) => brokerageAffiliationsApi.remove(id), onSuccess: invalidate });

  const columns: Column<BrokerageAffiliationDto>[] = useMemo(() => [
    {
      key: "name",
      header: "Name",
      render: (a) => {
        const to = a.partnerKind === "Broker" ? `/brokers/${a.partnerId}` : `/leads/${a.partnerId}`;
        return <Link to={to} className="font-semibold hover:text-gold-shadow">{a.partnerName ?? a.partnerId.slice(0, 8)}</Link>;
      },
    },
    { key: "kind", header: "Kind", render: (a) => <Badge status={a.partnerKind === "Broker" ? "gold" : "info"}>{a.partnerKind}</Badge> },
    {
      // Lead Conversion Router (WS-3) — the affiliated broker's payee type
      // (only meaningful for a Broker partner).
      key: "brokerType",
      header: "Type",
      render: (a) =>
        a.partnerKind === "Broker" ? (
          a.brokerType === "Junior" ? (
            <Badge status="gold">Junior</Badge>
          ) : (
            <span className="text-caption text-gray-mid">Independent</span>
          )
        ) : (
          "—"
        ),
    },
    { key: "type", header: "Affiliation Type", render: (a) => a.affiliationType },
    {
      key: "primary",
      header: "Primary",
      // The Primary column carries the toggle star itself: a filled gold star
      // for the current primary (click to unset), an empty star to promote a
      // non-primary affiliation. (There is one primary per partner — promoting
      // one demotes the rest server-side.) Read-only or ended rows fall back to
      // a static star / dash.
      render: (a) => {
        if (canUpdate && !a.endDate) {
          return (
            <Button
              size="sm"
              variant="text"
              aria-label={a.isPrimary ? "Unset primary brokerage" : "Set as primary"}
              disabled={setPrimary.isPending || clearPrimary.isPending}
              onClick={() => (a.isPrimary ? clearPrimary.mutate(a.id) : setPrimary.mutate(a.id))}
            >
              <Star
                size={14}
                strokeWidth={1.5}
                aria-hidden
                className={a.isPrimary ? "text-gold" : "text-gray-mid"}
                fill={a.isPrimary ? "currentColor" : "none"}
              />
            </Button>
          );
        }
        return a.isPrimary
          ? <Star size={14} strokeWidth={1.5} aria-label="Primary" className="text-gold" fill="currentColor" />
          : "—";
      },
    },
    { key: "start", header: "Start", render: (a) => (a.startDate ? date(a.startDate) : "—") },
    { key: "end", header: "End", render: (a) => (a.endDate ? formatCalendarDate(a.endDate) : "—") },
    ...(canUpdate ? [{
      key: "actions",
      header: "",
      render: (a: BrokerageAffiliationDto) => (
        <div className="flex items-center gap-space-1 justify-end">
          {!a.endDate && (
            <Button
              size="sm"
              variant="text"
              aria-label="End affiliation"
              disabled={end.isPending}
              onClick={async () => {
                const ok = await dialogs.confirm({
                  title: "End affiliation",
                  message: `End this affiliation${a.partnerName ? ` with ${a.partnerName}` : ""} as of today? The row stays on the timeline (it is not deleted).`,
                  confirmLabel: "End affiliation",
                });
                if (ok) end.mutate(a.id);
              }}
            >
              <X size={14} strokeWidth={1.5} aria-hidden />
            </Button>
          )}
          <Button
            size="sm"
            variant="text"
            aria-label="Remove affiliation"
            disabled={remove.isPending}
            onClick={async () => {
              const ok = await dialogs.confirm({
                title: "Remove affiliation",
                message: "Remove this affiliation (logged in error)?",
                confirmLabel: "Remove",
                destructive: true,
              });
              if (ok) remove.mutate(a.id);
            }}
          >
            <Trash2 size={14} strokeWidth={1.5} aria-hidden />
          </Button>
        </div>
      ),
    }] : []),
  ], [canUpdate, date, dialogs, setPrimary, clearPrimary, end, remove]);

  return (
    <div className="grid gap-space-3">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-space-1 text-caption text-gray-mid">
          <input type="checkbox" checked={includeEnded} onChange={(e) => setIncludeEnded(e.target.checked)} />
          Include ended affiliations
        </label>
        {canUpdate && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Building2 size={14} strokeWidth={1.5} aria-hidden /> Add affiliated partner
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={list.data ?? []}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage="No affiliated partners."
        rowKey={(a) => a.id}
      />

      {adding && (
        <AddPartnerModal
          brokerageId={brokerageId}
          brokerageName={brokerageName}
          onClose={() => setAdding(false)}
          onLinked={() => { setAdding(false); invalidate(); }}
        />
      )}
    </div>
  );
}

interface PartnerOption { id: string; name: string; }

function AddPartnerModal({
  brokerageId,
  brokerageName,
  onClose,
  onLinked,
}: {
  brokerageId: string;
  brokerageName: string;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [kind, setKind] = useState<PartnerKind>("Broker");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<PartnerOption | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Broker money core — a Broker partner is added through the shared
  // conversion component (type + per-deal absorb), with THIS firm pre-filled
  // as the covering brokerage; a Lead partner keeps the raw affiliation link.
  const [converting, setConverting] = useState<PartnerOption | null>(null);

  const results = useQuery({
    queryKey: ["partner-search", kind, q],
    queryFn: async (): Promise<PartnerOption[]> => {
      if (kind === "Broker") {
        const res = await brokersApi.list({ q: q || undefined, pageSize: 10 });
        return res.data.map((x) => {
          const { primary, secondary } = brokerPickerLabel(x);
          return { id: x.id, name: secondary ? `${primary} — ${secondary}` : primary };
        });
      }
      const res = await leadsApi.list({ q: q || undefined, pageSize: 10 });
      return res.data.map((x) => {
        const person = `${x.firstName} ${x.lastName}`.trim();
        const name = person || x.companyName || x.id.slice(0, 8);
        const extra = [person ? x.companyName : null, x.email].filter(Boolean).join(" · ");
        return { id: x.id, name: extra ? `${name} — ${extra}` : name };
      });
    },
  });

  const link = useMutation({
    mutationFn: () =>
      brokerageAffiliationsApi.link({ brokerageId, partnerId: selected!.id, partnerKind: kind }),
    onSuccess: onLinked,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <>
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Add affiliated partner"
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            loading={link.isPending}
            disabled={!selected}
            onClick={() => (kind === "Broker" ? setConverting(selected) : link.mutate())}
          >
            Link
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3">
        <FormField label="Partner kind">
          <Select value={kind} onChange={(e) => { setKind(e.target.value as PartnerKind); setSelected(null); }}>
            <option value="Broker">Broker</option>
            <option value="Lead">Lead</option>
          </Select>
        </FormField>
        <FormField label="Search" helperText={selected ? `Selected: ${selected.name}` : undefined}>
          <Input value={q} autoFocus placeholder={`Search ${kind.toLowerCase()}s…`} onChange={(e) => setQ(e.target.value)} />
        </FormField>
        <ul className="max-h-48 overflow-auto border border-border-light rounded-md">
          {results.isLoading && <li className="px-space-3 py-space-2 text-caption text-gray-mid">Searching…</li>}
          {results.data?.length === 0 && <li className="px-space-3 py-space-2 text-caption text-gray-mid">No matches.</li>}
          {results.data?.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                className={`w-full text-left px-space-3 py-space-2 text-small hover:bg-white-warm ${selected?.id === o.id ? "bg-white-warm font-semibold" : ""}`}
                onClick={() => setSelected(o)}
              >
                {o.name}
              </button>
            </li>
          ))}
        </ul>
        {error && <p className="text-label text-status-error" role="alert">{error}</p>}
      </div>
    </Modal>
    {converting && (
      <BrokerConversionModal
        brokerId={converting.id}
        brokerName={converting.name}
        fixedBrokerage={{ id: brokerageId, name: brokerageName }}
        onClose={() => setConverting(null)}
        onConverted={() => { setConverting(null); onLinked(); }}
      />
    )}
    </>
  );
}
